import React from 'react';
import {Box, Stack, Tooltip, Typography, alpha, useTheme} from '@mui/material';
import {
    NtsbMitigationTaxonomyCategoryDTO,
    SafetyRecommendationStatusGroup,
    SafetyRecommendationTaxonomySummaryDTO,
    TaxonomyDecadeCellDTO,
} from '../../@types/safety-recommendation';

/**
 * The same kind of fix, asked for decade after decade, and what became of it.
 *
 * <p>The NTSB says it has been recommending the same things for decades without the FAA acting. The
 * corpus spans 1967 to 2026, so the question is answerable, but only if a reader can see volume and
 * outcome at once: a classification with 200 recommendations of which four were declined reads very
 * differently from one with 200 of which sixty were.</p>
 *
 * <p>So a cell carries how many, and is shaded by the share of those that were closed unacceptable
 * or are still open. Those are the two ways an ask ends without the change being made, and both are
 * the issuer's own record rather than our inference.</p>
 */

/** The statuses that mean the ask did not result in the change being made, or has not yet. */
const NOT_ACTED_ON: SafetyRecommendationStatusGroup[] = ['CLOSED_UNACCEPTABLE', 'OPEN', 'OPEN_UNACCEPTABLE'];

export interface TopicOverTimeProps {
    summary: SafetyRecommendationTaxonomySummaryDTO | null;
    /** The taxonomy, for naming the rows and grouping them under their category. */
    categories: NtsbMitigationTaxonomyCategoryDTO[];
    /** Filters the table to one classification and decade. */
    onSelectCell?: (leafId: number, decade: number) => void;
    /** The classification and decade already filtered to, drawn as selected. */
    selectedLeafIds?: number[];
}

/** How many of a cell's recommendations ended without the change being made. */
export const notActedOn = (cell: TaxonomyDecadeCellDTO): number =>
    NOT_ACTED_ON.reduce((sum, group) => sum + (cell.byStatusGroup[group] ?? 0), 0);

/** The decades a grid covers, oldest first. */
export const decadesOf = (summary: SafetyRecommendationTaxonomySummaryDTO): number[] => {
    if (summary.firstDecade === null || summary.lastDecade === null) return [];
    const decades: number[] = [];
    for (let decade = summary.firstDecade; decade <= summary.lastDecade; decade += 10) {
        decades.push(decade);
    }
    return decades;
};

const TopicOverTime: React.FC<TopicOverTimeProps> = ({summary, categories, onSelectCell, selectedLeafIds = []}) => {
    const theme = useTheme();

    if (!summary || summary.cells.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{p: 2}}>
                Nothing matching this search is tagged with the taxonomy yet.
            </Typography>
        );
    }

    const decades = decadesOf(summary);
    const byLeaf = new Map<number, Map<number, TaxonomyDecadeCellDTO>>();
    summary.cells.forEach((cell) => {
        const row = byLeaf.get(cell.leafId) ?? new Map<number, TaxonomyDecadeCellDTO>();
        row.set(cell.decade, cell);
        byLeaf.set(cell.leafId, row);
    });
    // The busiest cell sets the top of the scale, so a grid of small numbers is still readable
    // rather than uniformly pale.
    const busiest = Math.max(...summary.cells.map((cell) => cell.total));

    const cellColour = (cell: TaxonomyDecadeCellDTO): string => {
        const share = cell.total === 0 ? 0 : notActedOn(cell) / cell.total;
        return alpha(theme.palette.error.main, 0.08 + 0.62 * share);
    };

    return (
        <Box sx={{overflow: 'auto', maxHeight: '100%'}}>
            <Box component="table" sx={{borderCollapse: 'separate', borderSpacing: '2px', width: 'max-content'}}>
                <Box component="thead">
                    <Box component="tr">
                        <Box component="th" sx={{position: 'sticky', left: 0, background: 'background.paper', zIndex: 2}} />
                        {decades.map((decade) => (
                            <Box
                                component="th"
                                key={decade}
                                sx={{px: 1, py: 0.5, minWidth: 64, textAlign: 'center'}}
                            >
                                <Typography variant="caption" color="text.secondary">{decade}s</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="tbody">
                    {categories.map((category) => {
                        const leaves = category.leaves.filter((leaf) => byLeaf.has(leaf.id));
                        if (leaves.length === 0) return null;
                        return (
                            <React.Fragment key={category.id}>
                                <Box component="tr">
                                    <Box component="td" colSpan={decades.length + 1} sx={{pt: 1.25, pb: 0.25}}>
                                        <Typography variant="overline" color="text.secondary">{category.name}</Typography>
                                    </Box>
                                </Box>
                                {leaves.map((leaf) => (
                                    <Box component="tr" key={leaf.id}>
                                        <Box
                                            component="th"
                                            scope="row"
                                            sx={{
                                                position: 'sticky', left: 0, zIndex: 1,
                                                background: theme.palette.background.paper,
                                                textAlign: 'left', pr: 1.5, maxWidth: 260,
                                                fontWeight: selectedLeafIds.includes(leaf.id) ? 600 : 400,
                                            }}
                                        >
                                            <Typography variant="body2" noWrap title={leaf.name}>{leaf.name}</Typography>
                                        </Box>
                                        {decades.map((decade) => {
                                            const cell = byLeaf.get(leaf.id)?.get(decade);
                                            if (!cell) {
                                                return <Box component="td" key={decade} sx={{minWidth: 64}} />;
                                            }
                                            const declined = notActedOn(cell);
                                            const label = `${leaf.name}, ${decade}s: ${cell.total} `
                                                + `recommendation${cell.total === 1 ? '' : 's'}, `
                                                + `${declined} not acted on`;
                                            return (
                                                <Box component="td" key={decade} sx={{p: 0}}>
                                                    <Tooltip title={label} describeChild>
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            aria-label={label}
                                                            onClick={(): void => onSelectCell?.(leaf.id, decade)}
                                                            sx={{
                                                                width: '100%', minWidth: 64, border: 0, borderRadius: 1,
                                                                py: 0.75, cursor: onSelectCell ? 'pointer' : 'default',
                                                                background: cellColour(cell),
                                                                // Size says how many, colour says how it went.
                                                                fontSize: 12 + 4 * (cell.total / busiest),
                                                                '&:hover': {outline: `2px solid ${theme.palette.primary.main}`},
                                                            }}
                                                        >
                                                            {cell.total}
                                                        </Box>
                                                    </Tooltip>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </Box>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1.5, px: 0.5}}>
                <Typography variant="caption" color="text.secondary">Shading: share closed unacceptable or still open</Typography>
                {[0, 0.25, 0.5, 0.75, 1].map((share) => (
                    <Box
                        key={share}
                        sx={{
                            width: 22, height: 12, borderRadius: 0.5,
                            background: alpha(theme.palette.error.main, 0.08 + 0.62 * share),
                        }}
                    />
                ))}
                <Typography variant="caption" color="text.secondary">none to all</Typography>
            </Stack>
        </Box>
    );
};

export default TopicOverTime;
